import React from 'react';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { ICustomizableElement } from '@insurancemenu/imenu-components-lib/dist/types';
import { observer } from 'mobx-react-lite';
import { colors } from '@insurancemenu/imenu-components-lib';
import { InfoButtonFilled } from '@insurancemenu/imenu-components-lib/dist/icons';
import {
  IconStyled,
  PRODUCT_LINE_CHIP_HEIGHT,
  ProductLineItemStyled,
  ProductLineSkeletonStyled,
  ProductLinesHintStyled,
  ProductLinesListStyled,
  ProductLinesSkeletonListStyled,
  SubsectionTitleStyled,
} from './styles';
import {
  AVAILABLE_PRODUCT_LINES_HINT_TEST_ID,
  AVAILABLE_PRODUCT_LINES_LIST_TEST_ID,
  AVAILABLE_PRODUCT_LINES_SKELETON_TEST_ID,
} from '../constants';

/** Skeleton widths that imitate real product line chip labels. */
const PRODUCT_LINE_SKELETON_WIDTHS = ['14rem', '11rem', '16rem', '12rem', '10rem'];

interface ProductLineItem {
  productLineId: ProductLineId;
  isAvailableWithCurrentConfiguration: boolean;
}

export interface Props extends ICustomizableElement {
  domainProductLineItems: ProductLineItem[];
  isLoading?: boolean;
}

const DomainProductLines: React.FC<Props> = observer(({ domainProductLineItems, className, isLoading = false }) => {
  const availableProductLineItems = domainProductLineItems.filter(
    (productLineItem) => productLineItem.isAvailableWithCurrentConfiguration,
  );
  const unavailableProductLineItems = domainProductLineItems.filter(
    (productLineItem) => !productLineItem.isAvailableWithCurrentConfiguration,
  );

  if (isLoading) {
    return (
      <ProductLinesSkeletonListStyled className={className} data-test-id={AVAILABLE_PRODUCT_LINES_SKELETON_TEST_ID}>
        {PRODUCT_LINE_SKELETON_WIDTHS.map((width) => (
          <ProductLineSkeletonStyled key={width} width={width} height={PRODUCT_LINE_CHIP_HEIGHT} />
        ))}
      </ProductLinesSkeletonListStyled>
    );
  }

  if (domainProductLineItems.length < 1) {
    return (
      <ProductLinesHintStyled className={className} data-test-id={AVAILABLE_PRODUCT_LINES_HINT_TEST_ID}>
        <IconStyled color={colors.yellow[600]} width="2.4rem" height="2.4rem" /> Complete the required fields to see
        available product lines.
      </ProductLinesHintStyled>
    );
  }

  return (
    <>
      <ProductLinesListStyled className={className} data-test-id={AVAILABLE_PRODUCT_LINES_LIST_TEST_ID}>
        {availableProductLineItems.map((productLineItem) => (
          <ProductLineItemStyled key={productLineItem.productLineId}>
            {PRODUCT_LINES_CONTENT_MAP[productLineItem.productLineId]?.icon('1.8rem')}
            {PRODUCT_LINES_CONTENT_MAP[productLineItem.productLineId]?.label}
          </ProductLineItemStyled>
        ))}
      </ProductLinesListStyled>
      {unavailableProductLineItems.length > 0 && (
        <>
          <SubsectionTitleStyled
            text={
              <>
                <InfoButtonFilled color={colors.yellow[600]} width="2.4rem" height="2.4rem" /> Unavailable For Current
                Configuration
              </>
            }
          />
          <ProductLinesListStyled className={className} data-test-id={AVAILABLE_PRODUCT_LINES_LIST_TEST_ID}>
            {unavailableProductLineItems.map((productLineItem) => (
              <ProductLineItemStyled key={productLineItem.productLineId} $unavailable>
                {PRODUCT_LINES_CONTENT_MAP[productLineItem.productLineId]?.icon('1.8rem')}
                {PRODUCT_LINES_CONTENT_MAP[productLineItem.productLineId]?.label}
              </ProductLineItemStyled>
            ))}
          </ProductLinesListStyled>
        </>
      )}
    </>
  );
});

export default DomainProductLines;
